/* eslint-disable no-param-reassign */
/*
https://github.com/ueberdosis/tiptap/issues/1036#issuecomment-1000983233
*/

import { ETiptapSelectorKey } from '@modules/documents/types/tiptap.types';
import { CommandProps, Extension, Extensions, isList, KeyboardShortcutCommand } from '@tiptap/core';
import { TextSelection, Transaction } from '@tiptap/pm/state';
import { TMap } from '@shared/types';

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    indent: {
      indent: () => ReturnType;
      outdent: () => ReturnType;
    };
  }
}

type IndentOptions = {
  names: Array<string>;
  minIndentLevel: number;
  maxIndentLevel: number;
  defaultIndentLevel: number;
  HTMLAttributes: TMap<string, any>;
};

const DEFAULT_INDENT_LEVEL = 0;

export const Indent = Extension.create<IndentOptions>({
  name: 'indent',

  addOptions() {
    return {
      names: ['paragraph'], // headings are not supported
      minIndentLevel: 0,
      maxIndentLevel: 100, // generous cap
      defaultIndentLevel: DEFAULT_INDENT_LEVEL,
      HTMLAttributes: {},
    };
  },

  addGlobalAttributes() {
    return [
      {
        types: this.options.names,
        attributes: {
          indentLevel: {
            default: this.options.defaultIndentLevel,
            renderHTML: (attrs) => {
              const level = Number(attrs.indentLevel) || DEFAULT_INDENT_LEVEL;
              return level > DEFAULT_INDENT_LEVEL
                ? {
                    'data-indent-level': String(level),
                    style: `--indent-level: ${level};`,
                  }
                : {};
            },
            parseHTML: (el) => {
              // prefer data attribute (Cheerio sets it)
              const a = el.getAttribute('data-indent-level') || '';
              const n = parseInt(a, 10);
              if (Number.isFinite(n) && n >= DEFAULT_INDENT_LEVEL) return n;
              return DEFAULT_INDENT_LEVEL;
            },
          },
        },
      },
    ];
  },

  addCommands() {
    const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));

    const setNodeIndentLevel = (tr: Transaction, pos: number, delta: number, min: number, max: number) => {
      const node = tr.doc?.nodeAt(pos);
      if (!node) return tr;
      const cur = Number(node.attrs.indentLevel) || DEFAULT_INDENT_LEVEL;
      const next = clamp(cur + delta, min, max);
      if (next === cur) return tr;
      return tr.setNodeMarkup(pos, node.type, { ...node.attrs, indentLevel: next }, node.marks);
    };

    const update = (tr: Transaction, options: IndentOptions, extensions: Extensions, delta: number): Transaction => {
      const { doc, selection } = tr;
      if (!doc || !(selection instanceof TextSelection)) return tr;
      const { from, to } = selection;
      doc.nodesBetween(from, to, (node, pos) => {
        if (options.names.includes(node.type.name)) {
          tr = setNodeIndentLevel(tr, pos, delta, options.minIndentLevel, options.maxIndentLevel);
          return false;
        }
        return !isList(node.type.name, extensions);
      });
      return tr;
    };

    return {
      indent:
        () =>
        ({ tr, state, dispatch, editor }: CommandProps) => {
          const nt = update(tr, this.options, editor.extensionManager.extensions, +1);
          if (nt.docChanged && dispatch) {
            dispatch(nt);
            return true;
          }
          return false;
        },
      outdent:
        () =>
        ({ tr, state, dispatch, editor }: CommandProps) => {
          const nt = update(tr, this.options, editor.extensionManager.extensions, -1);
          if (nt.docChanged && dispatch) {
            dispatch(nt);
            return true;
          }
          return false;
        },
    };
  },

  addKeyboardShortcuts() {
    return {
      Tab: getIndent(),
      'Shift-Tab': getOutdent(false),
      Backspace: getOutdent(true),
      'Mod-]': getIndent(),
      'Mod-[': getOutdent(false),
    };
  },

  onUpdate() {
    const { editor } = this;
    if (editor.isActive(ETiptapSelectorKey.LIST_ITEM)) {
      const node = editor.state.selection.$head.node();
      if (node.attrs.indentLevel) {
        editor.commands.updateAttributes(node.type.name, { indentLevel: DEFAULT_INDENT_LEVEL });
      }
    }
  },
});

const getIndent: () => KeyboardShortcutCommand =
  () =>
  ({ editor }) => {
    if (editor.can().sinkListItem(ETiptapSelectorKey.LIST_ITEM)) {
      return editor.chain().focus().sinkListItem(ETiptapSelectorKey.LIST_ITEM).run();
    }
    return editor.chain().focus().indent().run();
  };

const getOutdent: (outdentOnlyAtHead: boolean) => KeyboardShortcutCommand =
  (outdentOnlyAtHead) =>
  ({ editor }) => {
    if (outdentOnlyAtHead && editor.state.selection.$head.parentOffset > 0) {
      return false;
    }
    if (
      (!outdentOnlyAtHead || editor.state.selection.$head.parentOffset > 0) &&
      editor.can().liftListItem(ETiptapSelectorKey.LIST_ITEM)
    ) {
      return editor.chain().focus().liftListItem(ETiptapSelectorKey.LIST_ITEM).run();
    }
    return editor.chain().focus().outdent().run();
  };
